import { Skeleton } from "@/components/ui/skeleton";
import { panelPage, panelSurface, panelRow } from "@/lib/panel-ui";
import { ProducerDashboardSkeleton } from "./ProducerDashboardSkeleton";

export function ProducerEventsSkeleton() {
  return (
    <div className={panelPage} aria-busy="true" aria-label="Carregando eventos">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-1.5">
          <Skeleton className="h-8 w-44 rounded-2xl bg-secondary/80" />
          <Skeleton className="h-4 w-64 rounded-xl bg-secondary/50" />
        </div>
        <div className="flex items-center gap-2">
          <Skeleton className="h-10 w-36 rounded-full bg-secondary/70" />
        </div>
      </div>

      {/* Controles: Busca e Filtros */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Skeleton className="h-11 w-full sm:w-80 rounded-2xl bg-secondary/50 border border-border/40" />
        <div className="flex items-center gap-2">
          <Skeleton className="h-10 w-32 rounded-full bg-secondary/60" />
          <Skeleton className="h-10 w-32 rounded-full bg-secondary/60" />
        </div>
      </div>

      {/* Lista de Eventos (Cards 22px) */}
      <div className="space-y-3 pt-1">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className={`${panelRow} p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4`}
          >
            <div className="flex items-center gap-4 min-w-0">
              <Skeleton className="h-16 w-16 sm:h-20 sm:w-20 shrink-0 rounded-2xl bg-secondary/70" />
              <div className="space-y-2 min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <Skeleton className="h-5 w-20 rounded-full bg-secondary/60" />
                  <Skeleton className="h-4 w-28 rounded-md bg-secondary/40" />
                </div>
                <Skeleton className="h-5 sm:h-6 w-52 sm:w-80 rounded-xl bg-secondary/80" />
                <Skeleton className="h-3.5 w-40 rounded-lg bg-secondary/50" />
              </div>
            </div>

            <div className="flex items-center gap-3 sm:gap-6 shrink-0 justify-between sm:justify-end border-t sm:border-t-0 pt-3 sm:pt-0 border-border/40">
              <div className="space-y-1 text-right">
                <Skeleton className="h-5 w-24 rounded-lg bg-secondary/70 ml-auto" />
                <Skeleton className="h-3.5 w-20 rounded-md bg-secondary/40 ml-auto" />
              </div>
              <Skeleton className="h-9 w-28 rounded-full bg-secondary/60" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ProducerFinancialSkeleton() {
  return (
    <div className={panelPage} aria-busy="true" aria-label="Carregando dados financeiros">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-1.5">
          <Skeleton className="h-8 w-48 rounded-2xl bg-secondary/80" />
          <Skeleton className="h-4 w-72 rounded-xl bg-secondary/50" />
        </div>
        <div className="flex items-center gap-2">
          <Skeleton className="h-10 w-44 rounded-full bg-secondary/70" />
          <Skeleton className="h-10 w-28 rounded-full bg-secondary/60" />
        </div>
      </div>

      {/* Tabs Pílula */}
      <div className="flex items-center gap-2">
        <Skeleton className="h-9 w-32 rounded-full bg-secondary/70" />
        <Skeleton className="h-9 w-28 rounded-full bg-secondary/50" />
        <Skeleton className="h-9 w-36 rounded-full bg-secondary/50" />
      </div>

      {/* Grid de 4 KPIs */}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="flex h-[130px] flex-col justify-between rounded-[28px] border border-border/60 bg-white dark:bg-card p-5 sm:p-6"
          >
            <Skeleton className="h-3.5 w-28 rounded-lg bg-secondary/70" />
            <Skeleton className="h-8 w-36 rounded-xl bg-secondary/80" />
            <Skeleton className="h-3 w-40 rounded-lg bg-secondary/40" />
          </div>
        ))}
      </div>

      {/* Tabela de Conciliação / Extrato */}
      <div className={panelSurface}>
        <div className="border-b border-border/60 px-5 py-4 sm:px-6 flex items-center justify-between">
          <Skeleton className="h-4 w-40 rounded-lg bg-secondary/70" />
          <Skeleton className="h-8 w-32 rounded-full bg-secondary/50" />
        </div>
        <div className="divide-y divide-border/40">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="p-4 sm:px-6 flex items-center justify-between gap-4">
              <div className="space-y-1.5 min-w-0">
                <Skeleton className="h-4 w-48 sm:w-64 rounded-lg bg-secondary/80" />
                <Skeleton className="h-3 w-32 rounded-md bg-secondary/50" />
              </div>
              <div className="flex items-center gap-4 sm:gap-8 shrink-0">
                <Skeleton className="h-4 w-20 rounded-md bg-secondary/60 hidden sm:block" />
                <Skeleton className="h-5 w-24 rounded-lg bg-secondary/80" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function ProducerInboxSkeleton() {
  return (
    <div className={panelPage} aria-busy="true" aria-label="Carregando mensagens">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-1.5">
          <Skeleton className="h-8 w-44 rounded-2xl bg-secondary/80" />
          <Skeleton className="h-4 w-60 rounded-xl bg-secondary/50" />
        </div>
        <Skeleton className="h-10 w-36 rounded-full bg-secondary/60" />
      </div>

      {/* Split layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Painel lateral de mensagens */}
        <div className="lg:col-span-5 space-y-3">
          <Skeleton className="h-11 w-full rounded-2xl bg-secondary/50 border border-border/40" />
          <div className="flex gap-2">
            <Skeleton className="h-8 w-20 rounded-full bg-secondary/60" />
            <Skeleton className="h-8 w-24 rounded-full bg-secondary/50" />
            <Skeleton className="h-8 w-20 rounded-full bg-secondary/50" />
          </div>
          <div className="space-y-2 pt-1">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="rounded-2xl border border-border/60 bg-card p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <Skeleton className="h-4 w-32 rounded-md bg-secondary/80" />
                  <Skeleton className="h-3 w-16 rounded-md bg-secondary/40" />
                </div>
                <Skeleton className="h-4 w-48 rounded-md bg-secondary/60" />
                <Skeleton className="h-3 w-full rounded-md bg-secondary/40" />
              </div>
            ))}
          </div>
        </div>

        {/* Detalhe da conversa */}
        <div className="lg:col-span-7">
          <div className={`${panelSurface} h-[550px] flex flex-col justify-between p-5`}>
            <div className="border-b border-border/50 pb-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Skeleton className="h-10 w-10 rounded-full bg-secondary/70" />
                <div className="space-y-1">
                  <Skeleton className="h-4 w-36 rounded-md bg-secondary/80" />
                  <Skeleton className="h-3 w-24 rounded-md bg-secondary/50" />
                </div>
              </div>
              <Skeleton className="h-8 w-24 rounded-full bg-secondary/60" />
            </div>

            <div className="space-y-4 py-4">
              <div className="max-w-[70%] rounded-2xl bg-secondary/40 p-4 space-y-2">
                <Skeleton className="h-3.5 w-full rounded bg-secondary/60" />
                <Skeleton className="h-3.5 w-3/4 rounded bg-secondary/60" />
              </div>
              <div className="max-w-[70%] ml-auto rounded-2xl bg-secondary/30 border border-border/50 p-4 space-y-2">
                <Skeleton className="h-3.5 w-full rounded bg-secondary/60" />
                <Skeleton className="h-3.5 w-1/2 rounded bg-secondary/60" />
              </div>
            </div>

            <div className="border-t border-border/50 pt-4 flex gap-2">
              <Skeleton className="h-12 flex-1 rounded-2xl bg-secondary/50" />
              <Skeleton className="h-12 w-24 rounded-full bg-secondary/70" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ProducerSettingsSkeleton() {
  return (
    <div className={panelPage} aria-busy="true" aria-label="Carregando configurações">
      <div className="space-y-1.5">
        <Skeleton className="h-8 w-52 rounded-2xl bg-secondary/80" />
        <Skeleton className="h-4 w-72 rounded-xl bg-secondary/50" />
      </div>

      <div className="flex gap-2">
        <Skeleton className="h-9 w-28 rounded-full bg-secondary/70" />
        <Skeleton className="h-9 w-32 rounded-full bg-secondary/50" />
        <Skeleton className="h-9 w-28 rounded-full bg-secondary/50" />
      </div>

      <div className={panelSurface}>
        <div className="border-b border-border/60 px-6 py-4">
          <Skeleton className="h-5 w-44 rounded-lg bg-secondary/80" />
        </div>
        <div className="p-6 sm:p-7 space-y-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Skeleton className="h-4 w-28 rounded bg-secondary/60" />
              <Skeleton className="h-12 w-full rounded-2xl bg-secondary/50" />
            </div>
            <div className="space-y-2">
              <Skeleton className="h-4 w-28 rounded bg-secondary/60" />
              <Skeleton className="h-12 w-full rounded-2xl bg-secondary/50" />
            </div>
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-36 rounded bg-secondary/60" />
            <Skeleton className="h-24 w-full rounded-2xl bg-secondary/50" />
          </div>
          <Skeleton className="h-10 w-36 rounded-full bg-secondary/70 ml-auto" />
        </div>
      </div>
    </div>
  );
}

export function ProducerGenericSkeleton() {
  return (
    <div className={panelPage} aria-busy="true" aria-label="Carregando página">
      <div className="space-y-2">
        <Skeleton className="h-8 w-56 rounded-2xl bg-secondary/80" />
        <Skeleton className="h-4 w-80 rounded-xl bg-secondary/50" />
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-28 rounded-[28px] border border-border/60 bg-card p-5 space-y-2">
            <Skeleton className="h-3 w-20 rounded bg-secondary/60" />
            <Skeleton className="h-7 w-32 rounded-xl bg-secondary/80" />
          </div>
        ))}
      </div>

      <div className={`${panelSurface} p-6 sm:p-8 space-y-4`}>
        <Skeleton className="h-5 w-48 rounded-lg bg-secondary/80" />
        <Skeleton className="h-4 w-full rounded bg-secondary/50" />
        <Skeleton className="h-4 w-3/4 rounded bg-secondary/40" />
        <div className="h-48 rounded-2xl bg-secondary/30 border border-border/40 mt-4" />
      </div>
    </div>
  );
}

/**
 * Retorna o esqueleto de alta fidelidade mais adequado para o caminho atual ou de destino
 */
export function ProducerRouteSkeleton({ path }: { path: string }) {
  const normalized = path.toLowerCase();

  if (normalized.includes("dashboard")) {
    return <ProducerDashboardSkeleton />;
  }
  if (normalized.includes("eventos") || normalized.includes("events")) {
    return <ProducerEventsSkeleton />;
  }
  if (normalized.includes("financeiro") || normalized.includes("financial")) {
    return <ProducerFinancialSkeleton />;
  }
  if (normalized.includes("inbox") || normalized.includes("mensagens") || normalized.includes("messages")) {
    return <ProducerInboxSkeleton />;
  }
  if (
    normalized.includes("configuracoes") ||
    normalized.includes("settings") ||
    normalized.includes("organizacoes") ||
    normalized.includes("lista-de-interesse")
  ) {
    return <ProducerSettingsSkeleton />;
  }

  return <ProducerGenericSkeleton />;
}
